import React, { useState } from 'react';
import {
  AppLayoutToolbar,
  Button,
  Container,
  Form,
  Header,
  SpaceBetween,
  Flashbar,
  Alert,
  Link
} from '@amzn/awsui-components-core';
import { useFormState } from '../hooks/useFormState';
import { RemindersSettings } from './RemindersSettings';

interface RemindersFormProps {
  userAlias?: string;
  initialPreferences?: any;
}

export const RemindersForm: React.FC<RemindersFormProps> = ({ userAlias, initialPreferences }) => {
  const [flashbarItems, setFlashbarItems] = useState<any[]>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleNotification = (type: string, content: string, header?: string) => {
    setFlashbarItems([]);
    setTimeout(() => {
      setFlashbarItems([{
        type,
        content,
        ...(header && { header }),
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setFlashbarItems([]),
        id: `notification_${Date.now()}`
      }]);
    }, 0);
  };

  const wrappedHandleOptIn = () => {
    dismissTelegramBanner();
    setIsTransitioning(true);
    setTimeout(() => {
      handleOptIn();
    }, 300);
  };

  const wrappedHandleCancel = () => {
    dismissTelegramBanner();
    setIsTransitioning(true);
    setTimeout(() => {
      handleCancel();
    }, 300);
  };

  const wrappedHandleOptOut = async () => {
    dismissTelegramBanner();
    setIsTransitioning(true);
    setTimeout(async () => {
      await handleOptOut();
    }, 300);
  };

  const wrappedHandleSubmit = () => {
    dismissTelegramBanner();
    setIsTransitioning(true);
    setTimeout(() => {
      handleSubmit();
    }, 300);
  };

  const { formState, errors, isEditing, hasPreferences, showTelegramBanner, handleInputChange, handleSubmit, handleCancel, handleEdit, handleOptIn, handleOptOut, dismissTelegramBanner } = useFormState(initialPreferences, handleNotification);

  React.useEffect(() => {
    if (isTransitioning) {
      const timer = setTimeout(() => setIsTransitioning(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isEditing, hasPreferences]);

  if (!hasPreferences && !isEditing) {
    return (
      <AppLayoutToolbar
        notifications={<Flashbar items={flashbarItems} />}
        content={
          <div style={{
            animation: isTransitioning ? 'slideDownFade 0.3s ease-out forwards' : 'slideUpFade 0.3s ease-out',
          }}>
          <Container
            header={
              <Header
                variant="h1"
              >
                {userAlias ? `Hello ${userAlias}!` : 'Hello!'}
              </Header>
            }
          >
            <SpaceBetween size="l">
              <div>
                <p>Welcome to the On-Call Reminders Service!</p>
                <p>Never miss your on-call shift again. Our service sends you timely reminders before your scheduled shifts via SMS or Telegram.</p>
                <p>Click the button below to set up your reminder preferences.</p>
              </div>
              <Button variant="primary" onClick={wrappedHandleOptIn}>Get Started</Button>
            </SpaceBetween>
          </Container>
          <style>{`
            @keyframes slideUpFade {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes slideDownFade {
              from { opacity: 1; transform: translateY(0); }
              to { opacity: 0; transform: translateY(20px); }
            }
          `}</style>
          </div>
        }
        contentType="default"
        navigationHide
        toolsHide
      />
    );
  }

  return (
    <AppLayoutToolbar
      notifications={<Flashbar items={flashbarItems} />}
      content={
        <div style={{
          animation: isTransitioning ? 'slideDownFade 0.3s ease-out forwards' : 'slideUpFade 0.3s ease-out',
        }}>
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {isEditing ? (
                <>
                  <Button variant="link" onClick={wrappedHandleCancel}>Cancel</Button>
                  <Button variant="primary" onClick={wrappedHandleSubmit}>Save preferences</Button>
                </>
              ) : (
                <>
                  <Button variant="primary" onClick={() => { dismissTelegramBanner(); handleEdit(); }}>Edit preferences</Button>
                  <Button onClick={wrappedHandleOptOut}>Remove your data</Button>
                </>
              )}
            </SpaceBetween>
          }
          header={
            <Header
              description="Afraid of missing your on-call shift? Don't worry - set up custom reminders and stay on top of your upcoming shifts!"
              variant="h1"
            >
              {userAlias ? `Hello ${userAlias}!` : 'Hello!'}
            </Header>
          }
        >
          <SpaceBetween size="l">
            {showTelegramBanner && (
              <Alert
                type="warning"
                dismissible
                onDismiss={dismissTelegramBanner}
                header="Important: Start conversation with Telegram bot"
              >
                To receive notifications, you must start a conversation with <Link href="https://t.me/amazon_oncall_bot" external>@amazon_oncall_bot</Link> on Telegram. Otherwise, the bot won't be able to send you notifications.
              </Alert>
            )}
            <RemindersSettings
              formState={formState}
              errors={errors}
              onInputChange={handleInputChange}
              disabled={!isEditing}
            />
          </SpaceBetween>
        </Form>
        <style>{`
          @keyframes slideUpFade {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes slideDownFade {
            from { opacity: 1; transform: translateY(0); }
            to { opacity: 0; transform: translateY(20px); }
          }
          .awsui-flashbar-item {
            animation: fadeIn 0.6s ease-out !important;
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
        </div>
      }
      contentType="form"
      navigationHide
      toolsHide
    />
  );
};
